import {
  footerSectionSchema,
  sitePageSchema,
  type SiteDocument,
  type SitePage,
} from '../domains/site/schema';
import {
  buildGeneratedSiteFooterLegalLinks,
  buildGeneratedSiteFooterSupportLinks,
  resolveGeneratedSiteFooterProfile,
} from './generated-site-footer';

type EnsureSitePageFooterOptions = {
  resolvePageHref?: (page: SitePage) => string | undefined;
  privacyHref?: string;
  contactHref?: string;
  refreshExisting?: boolean;
};

function buildFooterLinkId(prefix: string, index: number) {
  return `${prefix}-${index + 1}`;
}

export function buildSiteFooterSection(
  document: SiteDocument,
  page: SitePage,
  options: EnsureSitePageFooterOptions = {},
) {
  const profile = resolveGeneratedSiteFooterProfile(document.metadata);
  const companyName = document.metadata.companyName?.trim() || document.siteName;

  return footerSectionSchema.parse({
    id: 'footer',
    type: 'footer',
    siteName: document.siteName,
    companyName,
    description: profile.description,
    goalLabel: document.metadata.goal,
    pageColumnTitle: profile.pageColumnTitle,
    legalColumnTitle: profile.legalColumnTitle,
    supportColumnTitle: profile.supportColumnTitle,
    supportFallbackMessage: profile.supportFallbackMessage,
    pageLinks: document.pages.map((item, index) => ({
      id: buildFooterLinkId('page', index),
      label: item.title,
      href: options.resolvePageHref?.(item) ?? item.path,
      current: item.id === page.id,
    })),
    legalLinks: buildGeneratedSiteFooterLegalLinks(
      document.metadata,
      options.privacyHref,
    ).map((link, index) => ({
      id: buildFooterLinkId('legal', index),
      label: link.label,
      href: link.href,
      current: false,
    })),
    supportLinks: buildGeneratedSiteFooterSupportLinks(
      document.metadata,
      page,
      options.contactHref,
    ).map((link, index) => ({
      id: buildFooterLinkId('support', index),
      label: link.label,
      href: link.href,
      current: false,
    })),
  });
}

export function ensureSitePageHasFooterSection(
  document: SiteDocument,
  page: SitePage,
  options: EnsureSitePageFooterOptions = {},
) {
  const footerSection = buildSiteFooterSection(document, page, options);

  if (page.sections.some((section) => section.type === 'footer')) {
    if (!options.refreshExisting) {
      return page;
    }

    return sitePageSchema.parse({
      ...page,
      sections: page.sections.map((section) =>
        section.type === 'footer'
          ? {
              ...footerSection,
              id: section.id,
              templateId: section.templateId,
              semanticType: section.semanticType,
            }
          : section,
      ),
    });
  }

  return sitePageSchema.parse({
    ...page,
    sections: [...page.sections, footerSection],
  });
}
